import { Card, Flex, Typography } from 'antd'
import { createStyles } from 'antd-style'
import {
  Activity,
  KeyRound,
  LayoutGrid,
  RefreshCw,
  Settings,
  ShieldCheck,
  Users,
  UsersRound,
  Webhook
} from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import type { AppDetailGroup, AppDetailSection } from '../utils/appDetailNav'

const { Text } = Typography

const ICONS: Record<AppDetailSection, ReactNode> = {
  overview: <LayoutGrid size={14} />,
  people: <Users size={14} />,
  groups: <UsersRound size={14} />,
  roles: <ShieldCheck size={14} />,
  oidc: <KeyRound size={14} />,
  scim: <RefreshCw size={14} />,
  credentials: <KeyRound size={14} />,
  webhooks: <Webhook size={14} />,
  outboundWebhooks: <Webhook size={14} />,
  activity: <Activity size={14} />,
  settings: <Settings size={14} />
}

const useStyles = createStyles(({ token, css }) => ({
  card: css`
    position: sticky;
    top: ${token.paddingMD}px;
  `,
  nav: css`
    display: flex;
    flex-direction: column;
    gap: ${token.marginSM}px;
  `,
  heading: css`
    padding: 0 ${token.paddingXS}px ${token.paddingXXS}px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${token.colorTextTertiary};
  `,
  item: css`
    display: flex;
    align-items: center;
    gap: ${token.marginXS}px;
    width: 100%;
    min-height: 26px;
    padding: 0 ${token.paddingXS}px;
    border: 0;
    border-radius: ${token.borderRadius}px;
    background: transparent;
    color: ${token.colorTextSecondary};
    font-size: 12.5px;
    text-align: start;
    cursor: pointer;
    transition:
      background ${token.motionDurationFast},
      color ${token.motionDurationFast};

    &:hover {
      background: ${token.colorFillTertiary};
      color: ${token.colorText};
    }
  `,
  itemActive: css`
    background: ${token.controlItemBgActive};
    color: ${token.colorText};
    font-weight: 500;

    &:hover {
      background: ${token.controlItemBgActiveHover};
      color: ${token.colorText};
    }
  `,
  label: css`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  count: css`
    margin-inline-start: auto;
    font-family: ${token.fontFamilyCode};
    font-variant-numeric: tabular-nums;
    font-size: 11px;
    color: ${token.colorTextTertiary};
  `
}))

type Props = {
  groups: AppDetailGroup[]
  active: AppDetailSection
  onSelect: (section: AppDetailSection) => void
  /** Optional trailing counts, e.g. people or roles on this app. */
  counts?: Partial<Record<AppDetailSection, number>>
}

export const AppDetailNav = ({ groups, active, onSelect, counts }: Props) => {
  const { t } = useTranslation()
  const { styles, cx } = useStyles()

  return (
    <Card
      size="small"
      variant="outlined"
      className={styles.card}
      styles={{ body: { padding: 8 } }}
    >
      <nav className={styles.nav} aria-label={t('workspace.apps.navLabel')}>
        {groups.map((group) => (
          <Flex vertical gap={1} key={group.key}>
            {group.heading ? (
              <Text className={styles.heading}>
                {t(`workspace.apps.navGroups.${group.heading}`)}
              </Text>
            ) : null}
            {group.items.map((section) => {
              const isActive = section === active
              const count = counts?.[section]

              return (
                <button
                  type="button"
                  key={section}
                  aria-current={isActive ? 'page' : undefined}
                  className={cx(styles.item, isActive && styles.itemActive)}
                  onClick={() => onSelect(section)}
                >
                  <span aria-hidden style={{ display: 'inline-flex' }}>
                    {ICONS[section]}
                  </span>
                  <span className={styles.label}>
                    {t(`workspace.apps.nav.${section}`)}
                  </span>
                  {typeof count === 'number' ? (
                    <span className={styles.count}>{count}</span>
                  ) : null}
                </button>
              )
            })}
          </Flex>
        ))}
      </nav>
    </Card>
  )
}
